import React, { useEffect, useRef, useState } from "react";
import PenTool from "lucide-react/dist/esm/icons/pen-tool";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import ExternalLink from "lucide-react/dist/esm/icons/external-link";
import DownloadIcon from "lucide-react/dist/esm/icons/download";
import Wrench from "lucide-react/dist/esm/icons/wrench";
import X from "lucide-react/dist/esm/icons/x";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import { formatApiError } from "../lib/errors";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export const REVU_PROTOCOL = "luxtender-revu";
const OK_KEY = "lt_revu_ok";        // user confirmed direct-open works on this machine
const NUDGE_KEY = "lt_revu_nudged"; // one-time "did it open?" prompt already shown

function b64url(s) {
  return btoa(unescape(encodeURIComponent(s)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Fetch a short-lived signed link and hand the PDF to the local Revu handler. */
export async function openInBluebeam(tenderId, drawing) {
  const { data } = await api.post(`/tenders/${tenderId}/drawings/${drawing.id}/desktop-link`);
  const openUrl = `${API_BASE}/drawings-open/${data.token}`;
  const proto = `${REVU_PROTOCOL}://${b64url(openUrl)}`;
  // Classic custom-protocol launch via a throwaway iframe — does not unload
  // the page when the handler isn't installed.
  const f = document.createElement("iframe");
  f.style.display = "none";
  f.src = proto;
  document.body.appendChild(f);
  setTimeout(() => f.remove(), 3000);
}

// ---- One-time Windows installer for the luxtender-revu:// protocol ----
const PS_WRAPPER = `param([string]$uri)
$dir = Join-Path $env:LOCALAPPDATA 'LuxTender'
$log = Join-Path $dir 'revu-open.log'
try {
  $prefix = 'luxtender-revu://'
  if ($uri.StartsWith($prefix)) { $p = $uri.Substring($prefix.Length) } else { $p = $uri }
  $p = $p.TrimEnd('/')
  $p = $p.Replace('-','+').Replace('_','/')
  switch ($p.Length % 4) { 2 { $p += '==' } 3 { $p += '=' } }
  $url = [System.Text.Encoding]::UTF8.GetString([System.Convert]::FromBase64String($p))
  $tmp = Join-Path $env:TEMP ('LuxTender_' + [System.Guid]::NewGuid().ToString('N') + '.pdf')
  Invoke-WebRequest -Uri $url -OutFile $tmp -UseBasicParsing
  $revu = $null
  foreach ($base in @($env:ProgramFiles, \${env:ProgramFiles(x86)})) {
    if ($base -and (Test-Path (Join-Path $base 'Bluebeam Software'))) {
      $found = Get-ChildItem -Path (Join-Path $base 'Bluebeam Software') -Recurse -Filter 'Revu.exe' -ErrorAction SilentlyContinue | Select-Object -First 1
      if ($found) { $revu = $found.FullName; break }
    }
  }
  if ($revu) { Start-Process -FilePath $revu -ArgumentList ('"' + $tmp + '"') }
  else { Start-Process $tmp }
} catch {
  Add-Content -Path $log -Value ((Get-Date).ToString() + ' ERROR ' + $_.Exception.Message)
}`;

function buildInstallerCmd() {
  const ps64 = btoa(unescape(encodeURIComponent(PS_WRAPPER)));
  return [
    "@echo off",
    "setlocal",
    'set "DIR=%LOCALAPPDATA%\\LuxTender"',
    'if not exist "%DIR%" mkdir "%DIR%"',
    `set "PS64=${ps64}"`,
    'powershell -NoProfile -Command "[IO.File]::WriteAllBytes([IO.Path]::Combine($env:LOCALAPPDATA,\'LuxTender\',\'revu-open.ps1\'), [Convert]::FromBase64String($env:PS64))"',
    'reg add "HKCU\\Software\\Classes\\luxtender-revu" /ve /t REG_SZ /d "URL:LuxTender Revu Protocol" /f >nul',
    'reg add "HKCU\\Software\\Classes\\luxtender-revu" /v "URL Protocol" /t REG_SZ /d "" /f >nul',
    'reg add "HKCU\\Software\\Classes\\luxtender-revu\\shell\\open\\command" /ve /t REG_SZ /d "powershell -NoProfile -ExecutionPolicy Bypass -File \\"%DIR%\\revu-open.ps1\\" \\"%%1\\"" /f >nul',
    "echo.",
    'echo LuxTender one-click "Open in Bluebeam" is now set up on this PC.',
    "echo You can close this window.",
    "pause",
    "",
  ].join("\r\n");
}

export function downloadBluebeamInstaller() {
  const blob = new Blob([buildInstallerCmd()], { type: "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "LuxTender-Bluebeam-Setup.cmd";
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

/** Reusable "how to set up one-click open" steps — used by the settings
 *  Connectors card and the first-time nudge sheet. */
export function BluebeamSetupSteps({ withDownload, downloadTestid = "bluebeam-setup-download" }) {
  const Step = ({ n, children }) => (
    <li className="flex gap-2.5">
      <span className="shrink-0 inline-flex items-center justify-center rounded-full bg-accent text-white" style={{ width: 18, height: 18, fontSize: 10, fontWeight: 700 }}>{n}</span>
      <span className="text-ink-2" style={{ fontSize: 12.5, lineHeight: 1.5 }}>{children}</span>
    </li>
  );
  return (
    <>
      <p className="text-ink-3 mb-3" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
        A browser cannot launch a desktop app on its own, so this one-time setup registers a
        small <span className="font-mono">luxtender-revu://</span> handler on <b>this Windows PC</b>.
        Once installed you won't need to do this again on this machine — "Open in Bluebeam" will
        download the drawing and open it straight in Revu.
      </p>
      <ol className="space-y-2.5 mb-4">
        <Step n={1}>Click <b>Download setup</b> to save <span className="font-mono">LuxTender-Bluebeam-Setup.cmd</span>.</Step>
        <Step n={2}>Double-click the file. Windows SmartScreen may warn — choose <b>More info → Run anyway</b> (it only edits your own user registry, no admin needed).</Step>
        <Step n={3}>A black window confirms setup, then closes. That's it — Bluebeam Revu must be installed.</Step>
        <Step n={4}>Back on any drawing, click <b>Open in Bluebeam</b>. The first time, tick <i>"Always allow"</i> in the browser's prompt.</Step>
      </ol>
      <div className="rounded-md border border-line bg-surface-2/50 px-3 py-2 text-ink-3" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
        Prefer no setup? Use <b>Download &amp; open in Bluebeam</b> on a drawing instead — it downloads the PDF and, if Bluebeam Revu is your default PDF app, opens there when you click the download.
      </div>
      {withDownload && (
        <button type="button" onClick={downloadBluebeamInstaller} className="mt-4 h-9 px-3 rounded-md bg-accent text-white inline-flex items-center gap-1.5 hover:opacity-90" style={{ fontSize: 13 }} data-testid={downloadTestid}>
          <DownloadIcon size={14} /> Download setup (.cmd)
        </button>
      )}
    </>
  );
}

// Custom high z-index shell so these dialogs sit ABOVE the PDF viewer (z-70).
function Sheet({ open, onClose, title, children, footer, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[85] bg-ink/50 flex items-start justify-center pt-24 px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }} data-testid={`${testid}-backdrop`}>
      <div className="w-full bg-surface border border-line rounded-lg shadow-card overflow-hidden" style={{ maxWidth: 520 }} data-testid={testid}>
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-line">
          <div className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
          <button type="button" onClick={onClose} className="h-7 w-7 inline-flex items-center justify-center rounded-md text-ink-3 hover:text-ink hover:bg-surface-2" aria-label="Close" data-testid={`${testid}-close`}><X size={15} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2 bg-surface-2/40">{footer}</div>}
      </div>
    </div>
  );
}

function SetupSheet({ open, onClose }) {
  return (
    <Sheet open={open} onClose={onClose} title="Set up one-click open in Bluebeam" testid="bluebeam-setup"
      footer={
        <>
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2" style={{ fontSize: 13 }} data-testid="bluebeam-setup-done">Done</button>
          <button type="button" onClick={downloadBluebeamInstaller} className="h-9 px-3 rounded-md bg-accent text-white inline-flex items-center gap-1.5 hover:opacity-90" style={{ fontSize: 13 }} data-testid="bluebeam-setup-download"><DownloadIcon size={14} /> Download setup (.cmd)</button>
        </>
      }>
      <BluebeamSetupSteps />
    </Sheet>
  );
}

/**
 * Shared Bluebeam launcher. Owns the one-time "did it open?" nudge and the
 * setup sheet, rendered high enough (z-85) to sit over the PDF viewer.
 * `launch(tenderId, drawing)` fires the direct-open protocol.
 */
export function useBluebeamLauncher() {
  const [setupOpen, setSetupOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const launch = async (tenderId, drawing) => {
    try {
      await openInBluebeam(tenderId, drawing);
    } catch (e) {
      toast.error(formatApiError(e, "Could not create the Bluebeam link"));
      return;
    }
    toast.message("Opening in Bluebeam…", { description: "If nothing happens, set up one-click open." });
    if (localStorage.getItem(OK_KEY) === "1" || localStorage.getItem(NUDGE_KEY) === "1") return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      localStorage.setItem(NUDGE_KEY, "1");
      setNudgeOpen(true);
    }, 2500);
  };

  const ui = (
    <>
      <Sheet open={nudgeOpen} onClose={() => setNudgeOpen(false)} title="Did Bluebeam open the drawing?" testid="bluebeam-nudge"
        footer={
          <>
            <button type="button" onClick={() => { localStorage.setItem(OK_KEY, "1"); setNudgeOpen(false); }} className="h-9 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2" style={{ fontSize: 13 }} data-testid="bluebeam-nudge-worked">Yes, it opened</button>
            <button type="button" onClick={() => { setNudgeOpen(false); setSetupOpen(true); }} className="h-9 px-3 rounded-md bg-accent text-white inline-flex items-center gap-1.5 hover:opacity-90" style={{ fontSize: 13 }} data-testid="bluebeam-nudge-setup"><Wrench size={14} /> No — set it up</button>
          </>
        }>
        <p className="text-ink-2" style={{ fontSize: 13, lineHeight: 1.55 }}>
          If Bluebeam Revu just opened the drawing, you're all set. If nothing happened, this PC needs
          a quick one-time setup so the browser can hand files straight to Revu.
        </p>
      </Sheet>
      <SetupSheet open={setupOpen} onClose={() => setSetupOpen(false)} />
    </>
  );

  return { launch, openSetup: () => setSetupOpen(true), ui };
}

/** "Open in Bluebeam" split button — direct launch + fallback menu. Requires `bb` from useBluebeamLauncher. */
export function BluebeamButton({ tenderId, drawing, onDownload, compact, bb }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const go = () => bb.launch(tenderId, drawing);

  const b = "inline-flex items-center gap-1 h-7 px-2 text-ink-2 hover:text-ink hover:bg-surface-2 border border-line";
  const menuItem = "w-full flex items-center gap-2 px-3 py-2 text-left text-ink-2 hover:bg-surface-2";

  return (
    <div className="inline-flex" style={{ fontSize: 11 }}>
      <button type="button" onClick={go} className={`${b} rounded-l`} title="Open this drawing directly in Bluebeam Revu" data-testid={`drawing-${drawing.id}-bluebeam`}>
        <PenTool size={11} /> {compact ? "Bluebeam" : "Open in Bluebeam"}
      </button>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={`${b} rounded-r border-l-0 px-1`} title="More Bluebeam options" data-testid={`drawing-${drawing.id}-bluebeam-menu`}>
            <ChevronDown size={12} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-1" style={{ fontSize: 12 }}>
          <button type="button" className={menuItem} onClick={() => { setMenuOpen(false); go(); }} data-testid={`drawing-${drawing.id}-bluebeam-direct`}>
            <ExternalLink size={13} className="text-accent" />
            <span>Open directly in Revu<br /><span className="text-ink-4" style={{ fontSize: 10.5 }}>One-time setup in Settings → Connectors</span></span>
          </button>
          <button type="button" className={menuItem} onClick={() => { setMenuOpen(false); onDownload?.(); }} data-testid={`drawing-${drawing.id}-bluebeam-download`}>
            <DownloadIcon size={13} className="text-ink-3" />
            <span>Download &amp; open in Bluebeam<br /><span className="text-ink-4" style={{ fontSize: 10.5 }}>Opens if Revu is your default PDF app</span></span>
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
